import { useState, useRef, useCallback, useEffect } from "react";
import { useClerk, useUser, SignIn, SignUp, useAuth } from "@clerk/clerk-react";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

// Helper — get a fresh session token for every API call
async function getAuthHeader(getToken) {
  const token = await getToken();
  if (!token) throw new Error("Not authenticated");
  return { Authorization: `Bearer ${token}` };
}

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Rajdhani:wght@400;500;600;700&family=Orbitron:wght@400;700;900&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --green:        #39FF14;
    --green-dim:    #2aaa10;
    --green-glow:   rgba(57,255,20,0.18);
    --green-faint:  rgba(57,255,20,0.06);
    --black:        #000000;
    --bg:           #070a06;
    --bg-card:      #0b0f0a;
    --bg-card2:     #0f1410;
    --border:       rgba(57,255,20,0.15);
    --border-bright:rgba(57,255,20,0.35);
    --text:         #e8f5e3;
    --text-muted:   #7aaa6a;
    --text-dim:     #6aaa55;
    --red:          #ff3b3b;
    --amber:        #ffb800;
    --mono:         'Share Tech Mono', monospace;
    --display:      'Orbitron', sans-serif;
    --body:         'Rajdhani', sans-serif;
    --radius:       2px;
    --glow:         0 0 12px rgba(57,255,20,0.3);
    --glow-strong:  0 0 24px rgba(57,255,20,0.5), 0 0 48px rgba(57,255,20,0.2);
  }

  html, body, #root {
    height: 100%;
    background: var(--black);
    color: var(--text);
    font-family: var(--body);
    font-size: 15px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
  }

  /* Scanline overlay */
  body::before {
    content: '';
    position: fixed;
    inset: 0;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0,0,0,0.08) 2px,
      rgba(0,0,0,0.08) 4px
    );
    pointer-events: none;
    z-index: 9999;
  }

  /* Vignette */
  body::after {
    content: '';
    position: fixed;
    inset: 0;
    background: radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.7) 100%);
    pointer-events: none;
    z-index: 9998;
  }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: var(--black); }
  ::-webkit-scrollbar-thumb { background: var(--green-dim); border-radius: 2px; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes loginShake {
    0%,100% { transform: translateX(0); }
    20%,60% { transform: translateX(-8px); }
    40%,80% { transform: translateX(8px); }
  }
  .login-shake { animation: loginShake 0.4s ease; }
  @keyframes scanIn {
    from { opacity: 0; clip-path: inset(0 0 100% 0); }
    to   { opacity: 1; clip-path: inset(0 0 0% 0); }
  }
  @keyframes pulse-green {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.4; }
  }
  @keyframes flicker {
    0%,100% { opacity:1 } 92% { opacity:1 } 93% { opacity:0.4 } 94% { opacity:1 } 96% { opacity:0.8 } 97% { opacity:1 }
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes progress-bar {
    from { width: 0%; }
    to   { width: 100%; }
  }
  @keyframes glitch {
    0%   { clip-path: inset(40% 0 61% 0); transform: translate(-2px, 0); }
    20%  { clip-path: inset(92% 0 1% 0);  transform: translate(2px, 0); }
    40%  { clip-path: inset(43% 0 1% 0);  transform: translate(0, 0); }
    60%  { clip-path: inset(25% 0 58% 0); transform: translate(2px, 0); }
    80%  { clip-path: inset(54% 0 7% 0);  transform: translate(-2px, 0); }
    100% { clip-path: inset(58% 0 43% 0); transform: translate(0, 0); }
  }
`;

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

function GreenBar({ pct, color }) {
  const c = color || (pct >= 76 ? '#39FF14' : pct >= 50 ? '#ffb800' : '#ff3b3b');
  return (
    <div style={{ height: 3, background: 'rgba(57,255,20,0.1)', borderRadius: 1, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${pct}%`, background: c, boxShadow: `0 0 6px ${c}`, transition: 'width 0.8s ease' }} />
    </div>
  );
}

function ConfidenceRow({ label, value }) {
  if (value === undefined || value === null) return null;
  const pct = Math.round(value * 100);
  const color = pct >= 76 ? '#39FF14' : pct >= 50 ? '#ffb800' : '#ff3b3b';
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color, fontWeight: 700 }}>{pct}%</span>
      </div>
      <GreenBar pct={pct} color={color} />
    </div>
  );
}

function Tag({ children, color }) {
  const c = color || 'var(--green)';
  return (
    <span style={{
      fontFamily: 'var(--mono)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.12em',
      color: c, border: `1px solid ${c}`, padding: '2px 7px', borderRadius: 1,
      background: `${c}11`,
    }}>{children}</span>
  );
}

function DataLine({ label, value, mono, highlight }) {
  if (!value) return null;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      padding: '9px 0', borderBottom: '1px solid var(--border)', gap: 12,
    }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0, marginTop: 1 }}>
        {label}
      </span>
      <span style={{
        fontFamily: mono ? 'var(--mono)' : 'var(--body)',
        fontSize: mono ? 12 : 14,
        fontWeight: highlight ? 700 : 600,
        color: highlight ? 'var(--green)' : 'var(--text)',
        textAlign: 'right',
        textShadow: highlight ? 'var(--glow)' : 'none',
      }}>{value}</span>
    </div>
  );
}

function Card({ children, style, glow }) {
  return (
    <div style={{
      background: 'var(--bg-card)',
      border: `1px solid ${glow ? 'var(--border-bright)' : 'var(--border)'}`,
      borderRadius: 'var(--radius)',
      padding: '22px 26px',
      boxShadow: glow ? 'inset 0 0 20px var(--green-faint)' : 'none',
      ...style,
    }}>
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: 'var(--mono)', fontSize: 9, textTransform: 'uppercase',
      letterSpacing: '0.2em', color: 'var(--green-dim)', marginBottom: 14,
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{ color: 'var(--green)', fontSize: 8 }}>▶</span>
      {children}
    </div>
  );
}

// ─── FEEDBACK PANEL ───────────────────────────────────────────────────────────

function FeedbackPanel({ result, imageHash, imageFile, onSubmitted, getToken }) {
  const [state, setState] = useState('idle'); // idle | open | submitting | done
  const [isCorrect, setIsCorrect] = useState(null);
  const [correction, setCorrection] = useState({
    make: '', model: '', year_range: '', color: '', trim: '', license_plate: '', notes: ''
  });

  async function handleSubmit(confirmed = false) {
    setState('submitting');
    try {
      const headers = await getAuthHeader(getToken);
      const correctionData = Object.fromEntries(Object.entries(correction).filter(([, v]) => v.trim()));
      const isCorrectVal = confirmed ? true : isCorrect === true ? true : false;
      let res;
      if (imageFile) {
        const form = new FormData();
        form.append('image', imageFile);
        form.append('image_hash', imageHash);
        form.append('is_correct', String(isCorrectVal));
        form.append('original_result', JSON.stringify(result));
        form.append('correction', JSON.stringify(correctionData));
        res = await fetch(`${API_BASE_URL}/feedback`, { method: 'POST', headers, body: form });
      } else {
        res = await fetch(`${API_BASE_URL}/feedback`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_hash: imageHash, is_correct: isCorrectVal, original_result: result, correction: correctionData }),
        });
      }
      if (!res.ok) throw new Error('Server error');
      setState('done');
      if (onSubmitted) onSubmitted();
    } catch {
      setState('open');
      alert('Failed to submit feedback. Please try again.');
    }
  }

  if (state === 'done') {
    return (
      <Card style={{ borderColor: 'var(--green-dim)', animation: 'fadeUp 0.4s ease' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color: 'var(--green)', fontSize: 18 }}>✓</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--green)' }}>
            FEEDBACK RECORDED — future analyses of this image will use your correction
          </span>
        </div>
      </Card>
    );
  }

  const inputStyle = {
    background: 'var(--black)',
    border: '1px solid var(--border)',
    borderRadius: 1,
    padding: '7px 10px',
    color: 'var(--text)',
    fontFamily: 'var(--mono)',
    fontSize: 12,
    width: '100%',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  return (
    <Card style={{ animation: 'fadeUp 0.4s ease' }}>
      <SectionLabel>Analyst Feedback</SectionLabel>

      {state === 'idle' && (
        <div>
          <p style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', marginBottom: 14 }}>
            Is this identification correct?
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => { setIsCorrect(true); handleSubmit(true); }} style={{
              flex: 1, padding: '10px 0', background: 'var(--green-faint)',
              border: '1px solid var(--green-dim)', borderRadius: 1, color: 'var(--green)',
              fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => e.target.style.background = 'var(--green-glow)'}
              onMouseLeave={e => e.target.style.background = 'var(--green-faint)'}
            >
              ✓ CONFIRMED
            </button>
            <button onClick={() => { setIsCorrect(false); setState('open'); }} style={{
              flex: 1, padding: '10px 0', background: 'rgba(255,59,59,0.06)',
              border: '1px solid rgba(255,59,59,0.3)', borderRadius: 1, color: 'var(--red)',
              fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => e.target.style.background = 'rgba(255,59,59,0.12)'}
              onMouseLeave={e => e.target.style.background = 'rgba(255,59,59,0.06)'}
            >
              ✗ INCORRECT
            </button>
          </div>
        </div>
      )}

      {state === 'open' && (
        <div style={{ animation: 'fadeUp 0.3s ease' }}>
          <p style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Add analyst notes to help improve future identifications
          </p>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 4 }}>
              Analyst Notes
            </label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
              placeholder="e.g. badge clearly visible on trunk — Toyota Camry XSE, 2021, Midnight Black Pearl"
              value={correction.notes}
              onChange={e => setCorrection(p => ({ ...p, notes: e.target.value }))}
              onFocus={e => e.target.style.borderColor = 'var(--green-dim)'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setState('idle')} style={{
              padding: '10px 20px', background: 'transparent',
              border: '1px solid var(--border)', borderRadius: 1, color: 'var(--text-muted)',
              fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em',
            }}>
              CANCEL
            </button>
            <button onClick={handleSubmit} style={{
              flex: 1, padding: '10px 0', background: 'var(--green-faint)',
              border: '1px solid var(--green-dim)', borderRadius: 1, color: 'var(--green)',
              fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => e.target.style.background = 'var(--green-glow)'}
              onMouseLeave={e => e.target.style.background = 'var(--green-faint)'}
            >
              SUBMIT CORRECTION
            </button>
          </div>
        </div>
      )}

      {state === 'submitting' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 12, height: 12, border: '2px solid var(--green-dim)', borderTopColor: 'var(--green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)' }}>TRANSMITTING FEEDBACK...</span>
        </div>
      )}
    </Card>
  );
}

// ─── RESULTS PANEL ────────────────────────────────────────────────────────────

function ResultsPanel({ result, imageHash, imageFile, getToken }) {
  const id = result?.identification;
  const [feedbackDone, setFeedbackDone] = useState(false);

  // Guard — if identification is missing entirely, render nothing rather than crashing
  if (!id) return null;

  const isLowConfidence = id._low_confidence === true;
  const challengeDisagreed = id._challengeDisagreed === true;
  const sanityFailed = id._sanityFailed === true;
  const conf = id.confidence || {};
  const overallPct = Math.round((conf.overall || 0) * 100);
  const overallColor = overallPct >= 76 ? '#39FF14' : overallPct >= 50 ? '#ffb800' : '#ff3b3b';

  // LOW CONFIDENCE result — show honest uncertainty card instead of bad identification
  if (isLowConfidence) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Card style={{ borderColor: 'rgba(255,184,0,0.4)', background: 'rgba(255,184,0,0.04)', animation: 'fadeUp 0.4s ease' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 18, color: '#ffb800' }}>⚠</span>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 13, color: '#ffb800', letterSpacing: '0.12em' }}>LOW CONFIDENCE RESULT</div>
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.9, marginBottom: 12 }}>
            {id._low_confidence_reason || 'Insufficient visual data to produce a reliable identification.'}
          </div>
          {(id.make || id.model) && (
            <div style={{ padding: '10px 14px', background: 'rgba(255,184,0,0.05)', border: '1px solid rgba(255,184,0,0.2)', borderRadius: 1, marginBottom: 12 }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: '#ffb800', letterSpacing: '0.15em', marginBottom: 6 }}>BEST GUESS — LOW RELIABILITY</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-muted)' }}>
                {[id.make, id.model, id.year_range].filter(Boolean).join(' ')}
              </div>
              {id.color && <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>{id.color}</div>}
            </div>
          )}
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', lineHeight: 1.7 }}>
            For better results: ensure the vehicle is clearly visible, well-lit, and not heavily occluded. Three-quarter front or rear angles typically yield the most reliable identifications.
          </div>
        </Card>
        {!feedbackDone && (
          <div style={{ animation: 'fadeUp 0.4s ease 0.2s both' }}>
            <FeedbackPanel result={id} imageHash={result.image_hash} imageFile={imageFile} onSubmitted={() => setFeedbackDone(true)} getToken={getToken} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── HEADLINE CARD ── */}
      <Card glow style={{ position: 'relative', overflow: 'hidden', animation: 'fadeUp 0.4s ease' }}>
        {/* Top accent bar */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${overallColor}, transparent)`, boxShadow: `0 0 12px ${overallColor}` }} />

        {/* Sanity check failure warning */}
        {sanityFailed && id._sanityIssues?.length > 0 && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(255,59,59,0.06)', border: '1px solid rgba(255,59,59,0.3)', borderRadius: 1, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ color: 'var(--red)', fontSize: 12, flexShrink: 0 }}>⚠</span>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--red)', lineHeight: 1.7, letterSpacing: '0.05em' }}>
              BODY TYPE CONFLICT — The identified vehicle type does not match visual scene analysis. Treat this identification with additional scrutiny.
              {id._sanityIssues.map((issue, i) => (
                <span key={i} style={{ color: 'var(--text-dim)', display: 'block', marginTop: 3 }}>{issue.detail}</span>
              ))}
            </div>
          </div>
        )}

        {/* Challenge pass disagreement warning */}
        {challengeDisagreed && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(255,184,0,0.06)', border: '1px solid rgba(255,184,0,0.3)', borderRadius: 1, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ color: '#ffb800', fontSize: 12, flexShrink: 0 }}>⚡</span>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: '#ffb800', lineHeight: 1.7, letterSpacing: '0.05em' }}>
              VERIFICATION CONFLICT — A secondary analysis pass produced a different result. Treat this identification with additional scrutiny.
              {id._challengePass?.challenge_reasoning && (
                <span style={{ color: 'var(--text-dim)', display: 'block', marginTop: 3 }}>{id._challengePass.challenge_reasoning}</span>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ flex: 1 }}>
            {/* Badges row */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {id.database_matched && <Tag>DB Validated</Tag>}
              {id._feedbackUsed && <Tag color="var(--amber)">Feedback Applied</Tag>}
              {id.low_confidence_warning && <Tag color="var(--red)">Contradictions Detected</Tag>}
              {id._detectedAngle && <Tag color="var(--text-muted)">{id._detectedAngle} view</Tag>}
            </div>

            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: 6 }}>
              {/* VEHICLE IDENTIFIED */}
            </div>
            <div style={{
              fontFamily: 'var(--display)', fontSize: 20, fontWeight: 700, lineHeight: 1.2,
              color: 'var(--text)', textShadow: '0 0 20px rgba(57,255,20,0.15)',
              letterSpacing: '0.05em',
            }}>
              {[id.year_range, id.make, id.model].filter(Boolean).join(' ') || 'UNKNOWN VEHICLE'}
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {id.generation && (
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)' }}>{id.generation}</span>
              )}
              {id.trim && (
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--green-dim)', border: '1px solid var(--green-dim)', padding: '0 5px' }}>{id.trim}</span>
              )}
              {id.body_style && (
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', textTransform: 'capitalize' }}>{id.body_style}</span>
              )}
            </div>
          </div>

          {/* Overall confidence dial */}
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{
              fontFamily: 'var(--display)', fontSize: 38, fontWeight: 900, lineHeight: 1,
              color: overallColor, textShadow: `0 0 20px ${overallColor}`,
              animation: 'flicker 8s ease infinite',
            }}>
              {overallPct}
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.15em' }}>% CONF</div>
          </div>
        </div>
      </Card>

      {/* ── CONTRADICTION WARNING ── */}
      {id.contradictions?.length > 0 && (
        <Card style={{ borderColor: 'rgba(255,184,0,0.3)', background: 'rgba(255,184,0,0.04)', animation: 'fadeUp 0.4s ease 0.05s both' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 10 }}>
            ⚠ ANALYST DISAGREEMENT DETECTED
          </div>
          {id.contradictions.map((c, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase' }}>{c.field}: </span>
              {c.note ? (
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)' }}>{c.note}</span>
              ) : (c.values || []).map((v, j) => (
                <span key={j} style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', marginRight: 8 }}>
                  {v.val} ({v.count}/{id._voteCount || 5})
                </span>
              ))}
            </div>
          ))}
        </Card>
      )}

      {/* ── CONFIDENCE BREAKDOWN ── */}
      <Card style={{ animation: 'fadeUp 0.4s ease 0.1s both' }}>
        <SectionLabel>Signal Confidence</SectionLabel>
        <ConfidenceRow label="Make"        value={conf.make} />
        <ConfidenceRow label="Model"       value={conf.model} />
        <ConfidenceRow label="Year Range"  value={conf.year_range} />
        <ConfidenceRow label="Generation"  value={conf.generation} />
        {id.trim && <ConfidenceRow label="Trim Level"  value={conf.trim} />}
        <ConfidenceRow label="Color"       value={conf.color} />
        {conf.license_plate > 0 && <ConfidenceRow label="Plate" value={conf.license_plate} />}
      </Card>

      {/* ── VEHICLE DATA ── */}
      <Card style={{ animation: 'fadeUp 0.4s ease 0.15s both' }}>
        <SectionLabel>Vehicle Data</SectionLabel>
        <DataLine label="Make"       value={id.make} highlight />
        <DataLine label="Model"      value={id.model} highlight />
        <DataLine label="Year Range" value={id.year_range} />
        <DataLine label="Generation" value={id.generation} />
        <DataLine label="Trim"       value={id.trim} />
        <DataLine label="Body Style" value={id.body_style} />
        <DataLine label="Size Class" value={id.size_class} />
        {id.license_plate && (
          <DataLine label="Plate" mono
            value={`${id.license_plate}${id.license_plate_state_or_country ? ` · ${id.license_plate_state_or_country}` : ''}`}
          />
        )}
      </Card>

      {/* ── COLOR ANALYSIS ── */}
      <Card style={{ animation: 'fadeUp 0.4s ease 0.2s both' }}>
        <SectionLabel>Color Analysis</SectionLabel>
        {id.color_official_name && (
          <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 6 }}>Official OEM Paint</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: 'var(--body)', fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{id.color_official_name}</span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--green)', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', padding: '1px 7px' }}>
                {id.color_official_code}
              </span>
            </div>
          </div>
        )}
        <DataLine label="Color"    value={[id.color, id.color_modifier].filter(Boolean).join(' · ')} />
        <DataLine label="Secondary" value={id.color_secondary} />
        {id.color_reasoning && (
          <div style={{ marginTop: 10, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.7, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
            {id.color_reasoning}
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <ConfidenceRow label="Color confidence" value={conf.color} />
        </div>
      </Card>

      {/* ── TRIM EVIDENCE ── */}
      {id.trim_confidence_reason && (
        <Card style={{ animation: 'fadeUp 0.4s ease 0.25s both' }}>
          <SectionLabel>Trim Evidence</SectionLabel>
          <p style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7 }}>{id.trim_confidence_reason}</p>
        </Card>
      )}

      {/* ── IDENTIFICATION MARKERS ── */}
      {id.identification_markers?.length > 0 && (
        <Card style={{ animation: 'fadeUp 0.4s ease 0.3s both' }}>
          <SectionLabel>Identification Markers</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {id.identification_markers.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: 10, marginTop: 2, flexShrink: 0 }}>›</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>{m}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── IMAGE QUALITY ── */}
      {id.image_quality && (
        <Card style={{ animation: 'fadeUp 0.4s ease 0.35s both' }}>
          <SectionLabel>Image Assessment</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {[
              ['Clarity', id.image_quality.clarity],
              ['Lighting', id.image_quality.lighting],
              ['Angle', id.image_quality.angle],
              ['Occlusion', id.image_quality.occlusion],
            ].map(([lbl, val]) => {
              const c = { excellent: '#39FF14', good: '#4f6ef7', fair: '#ffb800', poor: '#ff3b3b', none: '#39FF14', heavy: '#ff3b3b', partial: '#ffb800' }[val] || 'var(--text-dim)';
              return (
                <div key={lbl} style={{ textAlign: 'center', padding: '10px 0', background: 'var(--bg-card2)', border: '1px solid var(--border)', borderRadius: 1 }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>{lbl}</div>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: c, textTransform: 'capitalize', textShadow: `0 0 8px ${c}` }}>{val || '—'}</div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── NOTES ── */}
      {id.notes && (
        <Card style={{ borderColor: 'var(--border-bright)', animation: 'fadeUp 0.4s ease 0.4s both' }}>
          <SectionLabel>Analyst Notes</SectionLabel>
          <p style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.7 }}>{id.notes}</p>
        </Card>
      )}

      {/* ── REFERENCE IMAGE ── */}
      {id._referenceImage?.url && (
        <Card style={{ borderColor: 'var(--border-bright)', animation: 'fadeUp 0.4s ease 0.42s both', padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px 8px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.2em' }}>EXAMPLE PHOTO</div>
            <a href={`https://en.wikipedia.org/wiki/${encodeURIComponent(id._referenceImage.title)}`} target="_blank" rel="noopener noreferrer"
              style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--green)', letterSpacing: '0.1em', textDecoration: 'none', opacity: 0.7 }}>
              via Wikipedia ↗
            </a>
          </div>
          <img
            src={id._referenceImage.url}
            alt={id._referenceImage.title}
            style={{ width: '100%', display: 'block', maxHeight: 260, objectFit: 'cover', objectPosition: 'center' }}
            onError={e => { e.target.closest('[data-ref-card]')?.style && (e.target.closest('[data-ref-card]').style.display = 'none'); }}
          />
          <div style={{ padding: '8px 16px', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.08em' }}>
            {id._referenceImage.title} — for visual comparison only
          </div>
        </Card>
      )}

      {/* ── FEEDBACK ── */}
      {!feedbackDone && (
        <div style={{ animation: 'fadeUp 0.4s ease 0.45s both' }}>
          <FeedbackPanel result={id} imageHash={imageHash} imageFile={imageFile} onSubmitted={() => setFeedbackDone(true)} getToken={getToken} />
        </div>
      )}
      {feedbackDone && (
        <Card style={{ borderColor: 'var(--green-dim)', animation: 'fadeUp 0.3s ease' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--green)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>✓</span> FEEDBACK RECORDED
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── UPLOAD ZONE ──────────────────────────────────────────────────────────────

function UploadZone({ onFile, loading }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  const handle = useCallback((file) => {
    if (file && file.type.startsWith('image/')) onFile(file);
  }, [onFile]);

  return (
    <div
      onClick={() => !loading && inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); handle(e.dataTransfer.files[0]); }}
      style={{
        border: `1px dashed ${dragging ? 'var(--green)' : 'var(--border-bright)'}`,
        borderRadius: 2,
        padding: '48px 24px',
        textAlign: 'center',
        cursor: loading ? 'not-allowed' : 'pointer',
        background: dragging ? 'var(--green-faint)' : 'var(--bg-card)',
        transition: 'all 0.2s',
        boxShadow: dragging ? 'var(--glow-strong)' : 'none',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Corner decorations */}
      {['topleft','topright','bottomleft','bottomright'].map(pos => (
        <div key={pos} style={{
          position: 'absolute',
          width: 16, height: 16,
          [pos.includes('top') ? 'top' : 'bottom']: 10,
          [pos.includes('left') ? 'left' : 'right']: 10,
          borderTop: pos.includes('top') ? '1px solid var(--green-dim)' : 'none',
          borderBottom: pos.includes('bottom') ? '1px solid var(--green-dim)' : 'none',
          borderLeft: pos.includes('left') ? '1px solid var(--green-dim)' : 'none',
          borderRight: pos.includes('right') ? '1px solid var(--green-dim)' : 'none',
        }} />
      ))}

      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => handle(e.target.files[0])} />

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 24, height: 24, border: '2px solid var(--green-dim)', borderTopColor: 'var(--green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--green)', animation: 'pulse-green 1.5s ease infinite', letterSpacing: '0.15em' }}>
            ANALYZING IMAGE...
          </span>
        </div>
      ) : (
        <>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 28, color: 'var(--green-dim)', marginBottom: 12, lineHeight: 1 }}>⬡</div>
          <div style={{ fontFamily: 'var(--display)', fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: '0.15em', marginBottom: 6 }}>
            DROP IMAGE TO ANALYZE
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', letterSpacing: '0.1em' }}>
            OR CLICK TO SELECT — JPG, PNG, WEBP — MAX 20MB
          </div>
        </>
      )}
    </div>
  );
}

// DropZone is used in EnhancementScreen — same component as UploadZone
const DropZone = UploadZone;

// ─── IMAGE COMPARISON SLIDER ──────────────────────────────────────────────────

function ComparisonSlider({ original, enhanced }) {
  const [pos, setPos] = useState(50);
  const ref = useRef();

  function onMouseMove(e) {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)));
  }
  function onTouchMove(e) {
    const rect = ref.current.getBoundingClientRect();
    setPos(Math.max(0, Math.min(100, ((e.touches[0].clientX - rect.left) / rect.width) * 100)));
  }

  return (
    <div ref={ref} onMouseMove={onMouseMove} onTouchMove={onTouchMove}
      style={{ position: 'relative', borderRadius: 2, overflow: 'hidden', cursor: 'col-resize', userSelect: 'none', border: '1px solid var(--border)', lineHeight: 0 }}>
      <img src={`data:image/jpeg;base64,${enhanced}`} alt="Enhanced" style={{ width: '100%', display: 'block' }} />
      <div style={{ position: 'absolute', inset: 0, clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={`data:image/jpeg;base64,${original}`} alt="Original" style={{ width: '100%', display: 'block' }} />
      </div>
      {/* Divider */}
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${pos}%`, width: 2, background: 'var(--green)', boxShadow: 'var(--glow)', transform: 'translateX(-50%)' }}>
        <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 28, height: 28, borderRadius: '50%', background: 'var(--black)',
          border: '2px solid var(--green)', boxShadow: 'var(--glow)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--green)',
        }}>⇔</div>
      </div>
      {/* Labels */}
      <div style={{ position: 'absolute', bottom: 8, left: 8, fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.6)', padding: '2px 6px', borderRadius: 1 }}>ORIGINAL</div>
      <div style={{ position: 'absolute', bottom: 8, right: 8, fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--green)', background: 'rgba(0,0,0,0.6)', padding: '2px 6px', borderRadius: 1, textShadow: '0 0 6px var(--green)' }}>ENHANCED</div>
    </div>
  );
}

// ─── LOADING OVERLAY ──────────────────────────────────────────────────────────

const STAGES = [
  "UPLOADING IMAGE...",
  "ENHANCING IMAGE QUALITY...",
  "DETECTING VEHICLE ANGLE...",
  "VISUAL REASONING PASS...",
  "QUERYING VEHICLE DATABASE...",
  "RUNNING 5-WAY CONSENSUS VOTE...",
  "RESOLVING WEIGHTED CONSENSUS...",
  "FINALIZING REPORT...",
];

function LoadingOverlay({ active }) {
  const [stage, setStage] = useState(0);
  const [dots, setDots] = useState('');

  useEffect(() => {
    if (!active) return;
    setStage(0);
    const interval = setInterval(() => {
      setStage(s => (s + 1) % STAGES.length);
      setDots(d => d.length >= 3 ? '' : d + '.');
    }, 1800);
    return () => clearInterval(interval);
  }, [active]);

  if (!active) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, backdropFilter: 'blur(4px)',
    }}>
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{
          fontFamily: 'var(--display)', fontSize: 11, fontWeight: 700,
          color: 'var(--green)', letterSpacing: '0.3em', textTransform: 'uppercase',
          marginBottom: 24, textShadow: 'var(--glow)',
        }}>
          NODE // REVEAL
        </div>
        <div style={{ width: 200, height: 1, background: 'var(--border-bright)', margin: '0 auto 24px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg, transparent, var(--green), transparent)`, animation: 'progress-bar 1.8s ease infinite' }} />
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-muted)', letterSpacing: '0.1em', minHeight: 20 }}>
          {STAGES[stage]}{dots}
        </div>
      </div>
    </div>
  );
}

// ─── GENERAL FEEDBACK MODAL ───────────────────────────────────────────────────

const FEEDBACK_CATEGORIES = [
  { value: 'bug', label: 'Bug Report' },
  { value: 'accuracy', label: 'Analysis Accuracy' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'ui', label: 'UI / Usability' },
  { value: 'general', label: 'General Feedback' },
];

function LegalModal({ onClose, type }) {
  const isToS = type === 'tos';
  const title = isToS ? 'TERMS OF SERVICE' : 'PRIVACY POLICY';
  const effective = 'Effective: March 12, 2026';

  const tos = [
    { heading: '1. Acceptance of Terms', body: 'By accessing or using NODE // REVEAL ("the Service"), operated by Operation Node Inc., a 501(c)(3) nonprofit organization, you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.' },
    { heading: '2. Description of Service', body: 'NODE // REVEAL is an AI-powered vehicle and image analysis tool designed to assist investigative workflows. Results are probabilistic and generated by artificial intelligence. All identifications must be independently validated before use in any official, legal, or law enforcement capacity.' },
    { heading: '3. Eligibility', body: 'You must be at least 18 years of age to use the Service. By using the Service, you represent that you meet this requirement and have the legal capacity to enter into these Terms.' },
    { heading: '4. Permitted Use', body: 'The Service is intended for legitimate investigative, research, and analytical purposes. You agree not to use the Service to harass, stalk, or surveil individuals without lawful authority; to violate any applicable law or regulation; to upload images you do not have the right to submit; or to attempt to reverse-engineer, scrape, or abuse the Service infrastructure.' },
    { heading: '5. Account Responsibility', body: 'You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately at nate@operationnode.com if you suspect unauthorized access.' },
    { heading: '6. Credits and Subscriptions', body: 'Free tier accounts receive a limited number of credits per month that reset on a rolling basis. Paid subscriptions provide unlimited access and are billed through Stripe on a monthly or annual basis. Subscription fees are non-refundable except as required by applicable law. You may cancel your subscription at any time through the billing portal; cancellation takes effect at the end of the current billing period.' },
    { heading: '7. Intellectual Property', body: 'All content, code, design, and branding associated with NODE // REVEAL is the property of Operation Node Inc. and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without express written permission.' },
    { heading: '8. Disclaimer of Warranties', body: 'THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. OPERATION NODE INC. DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT AI RESULTS WILL BE ACCURATE. AI-GENERATED IDENTIFICATIONS ARE PROBABILISTIC AND MAY BE INCORRECT.' },
    { heading: '9. Limitation of Liability', body: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, OPERATION NODE INC. SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, INCLUDING RELIANCE ON AI-GENERATED RESULTS IN ANY OFFICIAL OR LEGAL CONTEXT.' },
    { heading: '10. Indemnification', body: 'You agree to indemnify and hold harmless Operation Node Inc., its officers, directors, and employees from any claims, damages, or expenses arising from your use of the Service, your violation of these Terms, or your infringement of any third-party rights.' },
    { heading: '11. Modifications', body: 'We reserve the right to modify these Terms at any time. Continued use of the Service after changes are posted constitutes acceptance of the revised Terms. We will make reasonable efforts to notify users of material changes.' },
    { heading: '12. Governing Law', body: 'These Terms are governed by the laws of the State of Florida, without regard to conflict of law principles. Any disputes shall be resolved in the courts of Duval County, Florida.' },
    { heading: '13. Contact', body: 'For questions about these Terms, contact us at nate@operationnode.com or through the feedback form in the application.' },
  ];

  const privacy = [
    { heading: '1. Information We Collect', body: 'Account Information: When you create an account, we collect your email address and display name through our authentication provider (Clerk). Payment Information: Subscription payments are processed by Stripe. We do not store your payment card details — only a Stripe customer ID and subscription status. Images: Images you upload are processed in memory to perform analysis and enhancement. We store a hash of the image (not the image itself) for feedback and improvement purposes. Usage Data: We collect usage statistics including the number of analyses and enhancements performed, and when you last used the Service.' },
    { heading: '2. How We Use Your Information', body: 'We use the information we collect to provide and operate the Service; to manage your account and subscription; to improve AI analysis accuracy through reviewed feedback; to communicate with you about your account or the Service; and to maintain security and prevent abuse.' },
    { heading: '3. Image Data', body: 'Images you upload are transmitted securely over HTTPS and processed in server memory. We do not permanently store your uploaded images. A SHA-256 hash of the image is stored to associate feedback corrections with future analyses of the same image. This hash cannot be used to reconstruct the original image.' },
    { heading: '4. Data Sharing', body: 'We do not sell your personal data. We share data only with the following service providers who help us operate the Service: Clerk (authentication), Stripe (payments), Supabase (database), Railway (server hosting), Cloudflare (frontend hosting and DNS), and Anthropic (AI analysis). Each provider is bound by their own privacy policies and applicable data protection laws.' },
    { heading: '5. Data Retention', body: 'Account data is retained for as long as your account is active. If you delete your account, we will delete your personal data within 30 days, except where retention is required by law. Analysis feedback corrections are retained to improve the Service and may be retained in anonymized form after account deletion.' },
    { heading: '6. Security', body: 'We implement industry-standard security measures including HTTPS encryption in transit, hashed credentials, and access controls. No method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.' },
    { heading: '7. Your Rights', body: 'Depending on your jurisdiction, you may have rights to access, correct, or delete your personal data; to object to or restrict certain processing; and to data portability. To exercise these rights, contact us at nate@operationnode.com. We will respond within 30 days.' },
    { heading: '8. Children\'s Privacy', body: 'The Service is not directed to individuals under the age of 18. We do not knowingly collect personal information from minors. If you believe a minor has provided us with personal information, contact us and we will delete it promptly.' },
    { heading: '9. Cookies', body: 'We use essential cookies required for authentication and session management through Clerk. We do not use advertising or tracking cookies. You can control cookie settings through your browser, though disabling essential cookies may affect Service functionality.' },
    { heading: '10. Changes to This Policy', body: 'We may update this Privacy Policy from time to time. We will notify users of material changes by posting the updated policy in the application. Your continued use of the Service after changes are posted constitutes acceptance.' },
    { heading: '11. Contact', body: 'For privacy-related questions or to exercise your data rights, contact Operation Node Inc. at nate@operationnode.com.' },
  ];

  const sections = isToS ? tos : privacy;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 600, backdropFilter: 'blur(6px)', padding: '24px 16px' }}>
      <div style={{ width: '100%', maxWidth: 680, maxHeight: '85vh', background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: 2, display: 'flex', flexDirection: 'column', position: 'relative', animation: 'fadeUp 0.3s ease' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--green), transparent)' }} />

        {/* Header */}
        <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: 'var(--display)', fontSize: 14, fontWeight: 700, color: 'var(--green)', letterSpacing: '0.15em', textShadow: 'var(--glow)' }}>{title}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.15em', marginTop: 4 }}>OPERATION NODE INC. — {effective}</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', borderRadius: 1, color: 'var(--text-dim)', fontFamily: 'var(--mono)', fontSize: 10, padding: '5px 12px', cursor: 'pointer', letterSpacing: '0.1em' }}>✕ CLOSE</button>
        </div>

        {/* Scrollable content */}
        <div style={{ overflowY: 'auto', padding: '20px 28px 28px', flex: 1 }}>
          {sections.map((s, i) => (
            <div key={i} style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--green)', letterSpacing: '0.12em', marginBottom: 6 }}>{s.heading}</div>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.9, letterSpacing: '0.02em' }}>{s.body}</p>
            </div>
          ))}
          <div style={{ marginTop: 28, paddingTop: 16, borderTop: '1px solid var(--border)', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textAlign: 'center', letterSpacing: '0.1em' }}>
            © 2026 OPERATION NODE INC. — 501(c)(3) NONPROFIT — ALL RIGHTS RESERVED
          </div>
        </div>
      </div>
    </div>
  );
}

function GeneralFeedbackModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', category: 'general', message: '' });
  const [state, setState] = useState('idle'); // idle | submitting | done | error
  const [errorMsg, setErrorMsg] = useState('');

  const inputStyle = {
    width: '100%', background: 'var(--black)', border: '1px solid var(--border)',
    borderRadius: 1, padding: '8px 11px', color: 'var(--text)',
    fontFamily: 'var(--mono)', fontSize: 12, outline: 'none', transition: 'border-color 0.2s',
  };

  async function handleSubmit() {
    if (!form.message.trim()) return;
    setState('submitting');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_BASE_URL}/general-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      setState('done');
    } catch (e) {
      setErrorMsg(e.message);
      setState('error');
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: 24, backdropFilter: 'blur(4px)',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: '100%', maxWidth: 480, background: 'var(--bg-card)',
        border: '1px solid var(--border-bright)', borderRadius: 2,
        position: 'relative', overflow: 'hidden', animation: 'fadeUp 0.3s ease',
      }}>
        {/* Top accent */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--green), transparent)' }} />

        <div style={{ padding: '24px 24px 20px' }}>
          {/* Title row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: 4 }}>
                ▶ Development Feedback
              </div>
              <div style={{ fontFamily: 'var(--display)', fontSize: 14, fontWeight: 700, color: 'var(--green)', letterSpacing: '0.1em', textShadow: 'var(--glow)' }}>
                SHARE YOUR FEEDBACK
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4 }}>✕</button>
          </div>

          {state === 'done' ? (
            <div style={{ textAlign: 'center', padding: '32px 0', animation: 'fadeUp 0.3s ease' }}>
              <div style={{ fontSize: 32, color: 'var(--green)', marginBottom: 14, textShadow: 'var(--glow)' }}>✓</div>
              <div style={{ fontFamily: 'var(--display)', fontSize: 13, fontWeight: 700, color: 'var(--green)', letterSpacing: '0.12em', marginBottom: 8 }}>FEEDBACK RECEIVED</div>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.8 }}>
                Thank you — your feedback has been submitted and will be reviewed by the Node//Reveal team.
              </p>
              <button onClick={onClose} style={{ marginTop: 20, padding: '9px 24px', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', borderRadius: 1, color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.1em' }}>CLOSE</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Category */}
              <div>
                <label style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 5 }}>Category</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {FEEDBACK_CATEGORIES.map(c => (
                    <button key={c.value} onClick={() => setForm(p => ({ ...p, category: c.value }))}
                      style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.08em', padding: '4px 10px', borderRadius: 1, cursor: 'pointer', transition: 'all 0.15s', background: form.category === c.value ? 'var(--green-faint)' : 'transparent', border: `1px solid ${form.category === c.value ? 'var(--green-dim)' : 'var(--border)'}`, color: form.category === c.value ? 'var(--green)' : 'var(--text-muted)' }}
                    >{c.label}</button>
                  ))}
                </div>
              </div>

              {/* Name + Email row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 5 }}>Name (optional)</label>
                  <input style={inputStyle} placeholder="Your name" value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    onFocus={e => e.target.style.borderColor = 'var(--green-dim)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'}
                  />
                </div>
                <div>
                  <label style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 5 }}>Email (optional)</label>
                  <input style={inputStyle} placeholder="For follow-up" type="email" value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    onFocus={e => e.target.style.borderColor = 'var(--green-dim)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'}
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 5 }}>
                  Feedback <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 100 }}
                  placeholder="Describe your experience, report a bug, or suggest an improvement..."
                  value={form.message}
                  onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  onFocus={e => e.target.style.borderColor = 'var(--green-dim)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'}
                />
              </div>

              {state === 'error' && (
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--red)' }}>✗ {errorMsg}</div>
              )}

              <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
                <button onClick={onClose} style={{ padding: '9px 18px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 1, color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em' }}>
                  CANCEL
                </button>
                <button onClick={handleSubmit} disabled={!form.message.trim() || state === 'submitting'}
                  style={{ flex: 1, padding: '9px 0', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', borderRadius: 1, color: form.message.trim() ? 'var(--green)' : 'var(--text-dim)', fontFamily: 'var(--mono)', fontSize: 11, cursor: form.message.trim() ? 'pointer' : 'not-allowed', letterSpacing: '0.1em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: !form.message.trim() ? 0.5 : 1 }}
                >
                  {state === 'submitting'
                    ? <><div style={{ width: 12, height: 12, border: '2px solid var(--green-dim)', borderTopColor: 'var(--green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />SUBMITTING...</>
                    : 'SUBMIT FEEDBACK'
                  }
                </button>
              </div>

              <p style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', lineHeight: 1.6 }}>
                This feedback is for application improvement only and does not affect AI analysis results.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── AUTH SCREEN (uses Clerk's hosted components) ────────────────────────────

function AuthScreen() {
  const [tab, setTab] = useState('login');
  return (
    <div style={{ minHeight: '100vh', background: '#070a06', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <style>{css}</style>
      <div style={{ position: 'fixed', inset: 0, backgroundImage: 'linear-gradient(rgba(57,255,20,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(57,255,20,0.03) 1px, transparent 1px)', backgroundSize: '40px 40px', pointerEvents: 'none' }} />

      <div style={{ textAlign: 'center', marginBottom: 36, animation: 'fadeUp 0.4s ease' }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 26, fontWeight: 900, color: '#39FF14', letterSpacing: '0.15em', textShadow: '0 0 24px rgba(57,255,20,0.5)', marginBottom: 6 }}>
          NODE // REVEAL
        </div>
        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 10, color: '#7aaa6a', letterSpacing: '0.3em' }}>
          AI VEHICLE ANALYSIS SYSTEM
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 0, borderBottom: '1px solid rgba(57,255,20,0.1)', width: '100%', maxWidth: 420 }}>
        {['login', 'register'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ flex: 1, padding: '9px 0', background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === t ? '#39FF14' : 'transparent'}`, color: tab === t ? '#39FF14' : '#7aaa6a', fontFamily: "'Share Tech Mono', monospace", fontSize: 11, letterSpacing: '0.15em', cursor: 'pointer', marginBottom: -1, transition: 'all 0.15s', textTransform: 'uppercase' }}>
            {t === 'login' ? 'SIGN IN' : 'REGISTER'}
          </button>
        ))}
      </div>

      {/* Clerk components — styled to match NODE branding via appearance prop */}
      <div style={{ width: '100%', maxWidth: 420, animation: 'fadeUp 0.4s ease 0.1s both' }}>
        {tab === 'login'
          ? <SignIn appearance={{ variables: { colorPrimary: '#39FF14', colorBackground: '#0b0f0a', colorText: '#e8f5e3', colorInputBackground: '#070a06', colorInputText: '#e8f5e3', borderRadius: '2px', fontFamily: "'Share Tech Mono', monospace" }, elements: { card: { border: '1px solid rgba(57,255,20,0.2)', boxShadow: 'none', borderTop: '2px solid #39FF14' }, headerTitle: { color: '#39FF14' }, socialButtonsBlockButton: { border: '1px solid rgba(57,255,20,0.2)', background: 'rgba(57,255,20,0.04)', color: '#e8f5e3' } } }} />
          : <SignUp appearance={{ variables: { colorPrimary: '#39FF14', colorBackground: '#0b0f0a', colorText: '#e8f5e3', colorInputBackground: '#070a06', colorInputText: '#e8f5e3', borderRadius: '2px', fontFamily: "'Share Tech Mono', monospace" }, elements: { card: { border: '1px solid rgba(57,255,20,0.2)', boxShadow: 'none', borderTop: '2px solid #39FF14' }, headerTitle: { color: '#39FF14' } } }} />
        }
      </div>

      <div style={{ marginTop: 24, fontFamily: "'Share Tech Mono', monospace", fontSize: 9, color: '#6aaa55', letterSpacing: '0.1em', textAlign: 'center' }}>
        NEW ACCOUNTS RECEIVE 25 FREE CREDITS/MONTH
      </div>
    </div>
  );
}

// ─── CREDITS BADGE ────────────────────────────────────────────────────────────

function SubscriptionBadge({ profile, loading }) {
  const subscribed = profile?.subscription_tier && profile.subscription_tier !== 'free'
    && profile.subscription_expires_at && new Date(profile.subscription_expires_at) > new Date();
  const total = profile?.total_credits ?? '—';
  const low = !subscribed && typeof total === 'number' && total <= 2;
  const color = subscribed ? '#39FF14' : low ? '#ff3b3b' : '#39FF14';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${low ? 'rgba(255,59,59,0.3)' : 'rgba(57,255,20,0.2)'}`, borderRadius: 2, padding: '4px 12px', background: low ? 'rgba(255,59,59,0.05)' : 'rgba(57,255,20,0.04)' }}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="5" stroke={color} strokeWidth="1.2" opacity="0.6"/>
        <path d="M4 6h4M6 4v4" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.8"/>
      </svg>
      <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 11, color, letterSpacing: '0.08em' }}>
        {loading ? '...' : `${total} CR`}
      </span>
    </div>
  );
}

// ─── ENHANCEMENT MODULE SCREEN ────────────────────────────────────────────────

function EnhancementScreen({ onBack, credits, onCreditsUpdate, getToken, onSubscribe }) {
  const [file, setFile] = useState(null); // eslint-disable-line no-unused-vars
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleFile(f) {
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setError(null);
    setLoading(true);

    try {
      const headers = await getAuthHeader(getToken);
      const form = new FormData();
      form.append('image', f);
      const res = await fetch(`${API_BASE_URL}/enhance-only`, { method: 'POST', headers, body: form });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 402) throw new Error('INSUFFICIENT_CREDITS');
        throw new Error(data.error || `Server error: ${res.status}`);
      }
      setResult(data);
      if (data.credits_remaining !== undefined) onCreditsUpdate(data.credits_remaining);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function downloadEnhanced() {
    if (!result) return;
    const a = document.createElement('a');
    a.href = `data:image/jpeg;base64,${result.enhanced_b64}`;
    a.download = `enhanced_${file?.name || 'image.jpg'}`;
    a.click();
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      {loading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--display)', fontSize: 11, fontWeight: 700, color: 'var(--green)', letterSpacing: '0.3em', marginBottom: 24, textShadow: 'var(--glow)' }}>ENHANCING IMAGE...</div>
            <div style={{ width: 200, height: 1, background: 'var(--border-bright)', margin: '0 auto', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, var(--green), transparent)', animation: 'progress-bar 1.8s ease infinite' }} />
            </div>
          </div>
        </div>
      )}

      <header style={{ borderBottom: '1px solid var(--border)', padding: '0 28px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--black)', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontFamily: 'var(--display)', fontSize: 16, fontWeight: 900, color: 'var(--green)', letterSpacing: '0.15em', textShadow: 'var(--glow)' }}>NODE // REVEAL</div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', letterSpacing: '0.2em' }}>IMAGE ENHANCEMENT</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <SubscriptionBadge profile={credits} />
          <button onClick={onBack} style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', background: 'transparent', border: '1px solid var(--border)', borderRadius: 1, padding: '5px 12px', cursor: 'pointer', letterSpacing: '0.1em' }}>← MODULES</button>
        </div>
      </header>

      <main style={{ flex: 1, padding: '32px 28px', maxWidth: 960, margin: '0 auto', width: '100%' }}>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', letterSpacing: '0.3em', marginBottom: 8 }}>IMAGE ENHANCEMENT MODULE</div>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '0.06em' }}>ENHANCE & DOWNLOAD</h2>
          <p style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.8 }}>
            Upload a vehicle or surveillance image to apply AI-powered enhancement — noise reduction, contrast correction, and detail sharpening — then download the cleaned image. Costs 1 credit.
          </p>
        </div>

        {/* Cost notice */}
        <div style={{ border: '1px solid rgba(57,255,20,0.15)', background: 'rgba(57,255,20,0.03)', borderRadius: 2, padding: '10px 14px', marginBottom: 24, display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--green)' }}>◆ 1 CREDIT</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', letterSpacing: '0.05em' }}>PER ENHANCEMENT — STANDARD + SURVEILLANCE AUTO-DETECTED</span>
        </div>

        {error === 'INSUFFICIENT_CREDITS' ? (
          <div style={{ border: '1px solid rgba(255,59,59,0.3)', background: 'rgba(255,59,59,0.05)', borderRadius: 2, padding: '20px 24px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--display)', fontSize: 14, color: '#ff3b3b', letterSpacing: '0.15em', marginBottom: 10 }}>INSUFFICIENT CREDITS</div>
            <p style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 16 }}>You need at least 1 credit to enhance an image.</p>
            <button onClick={onSubscribe} style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--green)', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', borderRadius: 1, padding: '8px 20px', cursor: 'pointer', letterSpacing: '0.1em' }}>↑ SUBSCRIBE</button>
          </div>
        ) : !result ? (
          <DropZone onFile={handleFile} loading={loading} />
        ) : (
          <div style={{ animation: 'fadeUp 0.4s ease' }}>
            <ComparisonSlider original={result.enhanced_b64 ? btoa(String.fromCharCode(...new Uint8Array())) : ''} enhanced={result.enhanced_b64} original2={preview} />
            {/* Show before/after using the original preview URL and enhanced b64 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.2em', marginBottom: 8 }}>ORIGINAL</div>
                <img src={preview} alt="Original" style={{ width: '100%', borderRadius: 2, border: '1px solid var(--border)' }} />
              </div>
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--green)', letterSpacing: '0.2em', marginBottom: 8 }}>ENHANCED</div>
                <img src={`data:image/jpeg;base64,${result.enhanced_b64}`} alt="Enhanced" style={{ width: '100%', borderRadius: 2, border: '1px solid var(--border-bright)' }} />
              </div>
            </div>

            {result.enhancement_info && (
              <div style={{ marginTop: 20, border: '1px solid var(--border)', borderRadius: 2, padding: '14px 18px', background: 'var(--bg-card)' }}>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.2em', marginBottom: 10 }}>ENHANCEMENT DETAILS</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {Object.entries(result.enhancement_info).map(([k, v]) => (
                    <div key={k}>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{k.replace(/_/g, ' ')}</div>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--green)', marginTop: 2 }}>{String(v)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button onClick={downloadEnhanced} style={{ flex: 1, padding: '12px 0', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', borderRadius: 1, color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.15em', cursor: 'pointer', textShadow: 'var(--glow)' }}>
                ↓ DOWNLOAD ENHANCED IMAGE
              </button>
              <button onClick={() => { setResult(null); setFile(null); setPreview(null); setError(null); }}
                style={{ padding: '12px 20px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 1, color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.1em', cursor: 'pointer' }}>
                NEW IMAGE
              </button>
            </div>
          </div>
        )}

        {error && error !== 'INSUFFICIENT_CREDITS' && (
          <div style={{ marginTop: 20, border: '1px solid rgba(255,59,59,0.3)', background: 'rgba(255,59,59,0.05)', borderRadius: 2, padding: '12px 16px', fontFamily: 'var(--mono)', fontSize: 11, color: '#ff3b3b' }}>✗ {error}</div>
        )}
      </main>
    </div>
  );
}

// ─── BUY CREDITS MODAL ───────────────────────────────────────────────────────

const SUBSCRIPTION_PLANS = [
  {
    id: 'monthly',
    label: 'Monthly',
    price: '$4.99',
    period: '/month',
    description: 'Unlimited analyses & enhancements',
    badge: null,
  },
  {
    id: 'annual',
    label: 'Annual',
    price: '$49.99',
    period: '/year',
    description: 'Unlimited analyses & enhancements',
    badge: 'SAVE 17%',
  },
];

function SubscribeModal({ onClose, getToken, currentProfile }) {
  const [selected, setSelected] = useState('annual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  const isSubscribed = currentProfile?.subscription_tier && currentProfile.subscription_tier !== 'free'
    && currentProfile.subscription_expires_at && new Date(currentProfile.subscription_expires_at) > new Date();

  async function handleSubscribe() {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeader(getToken);
      const res = await fetch(`${API_BASE_URL}/payments/create-checkout-session`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId: selected }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  async function handleManage() {
    setPortalLoading(true);
    try {
      const headers = await getAuthHeader(getToken);
      const res = await fetch(`${API_BASE_URL}/payments/portal`, { method: 'POST', headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to open portal');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setPortalLoading(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500, backdropFilter: 'blur(6px)' }}>
      <div style={{ width: '100%', maxWidth: 460, background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: 2, padding: '32px 28px', position: 'relative', animation: 'fadeUp 0.3s ease' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--green), transparent)' }} />

        <div style={{ fontFamily: 'var(--display)', fontSize: 14, fontWeight: 700, color: 'var(--green)', letterSpacing: '0.15em', marginBottom: 6, textShadow: 'var(--glow)' }}>
          {isSubscribed ? 'MANAGE SUBSCRIPTION' : 'UPGRADE TO UNLIMITED'}
        </div>

        {isSubscribed ? (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 20 }}>
              You have an active <span style={{ color: 'var(--green)' }}>{currentProfile.subscription_tier.toUpperCase()}</span> subscription.
              {currentProfile.subscription_expires_at && (
                <> Renews {new Date(currentProfile.subscription_expires_at).toLocaleDateString()}.</>
              )}
            </div>
            <button onClick={handleManage} disabled={portalLoading}
              style={{ width: '100%', padding: '11px 0', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', borderRadius: 1, color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.12em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 10 }}>
              {portalLoading
                ? <><div style={{ width: 12, height: 12, border: '2px solid var(--green-dim)', borderTopColor: 'var(--green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />OPENING...</>
                : '→ MANAGE / CANCEL ON STRIPE'
              }
            </button>
            <button onClick={onClose}
              style={{ width: '100%', padding: '10px 0', background: 'transparent', border: '1px solid var(--border)', borderRadius: 1, color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em' }}>
              CLOSE
            </button>
          </div>
        ) : (
          <>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', marginBottom: 22, lineHeight: 1.7 }}>
              Unlimited vehicle analyses and image enhancements. Cancel anytime.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
              {SUBSCRIPTION_PLANS.map(plan => (
                <div key={plan.id} onClick={() => setSelected(plan.id)}
                  style={{ border: `1px solid ${selected === plan.id ? 'var(--green)' : 'var(--border)'}`, borderRadius: 2, padding: '16px 18px', cursor: 'pointer', background: selected === plan.id ? 'var(--green-faint)' : 'transparent', transition: 'all 0.15s', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: selected === plan.id ? 'inset 0 0 0 1px rgba(57,255,20,0.15)' : 'none', position: 'relative' }}>
                  {plan.badge && (
                    <div style={{ position: 'absolute', top: -10, right: 14, background: 'var(--green)', color: '#070a06', fontFamily: 'var(--mono)', fontSize: 8, fontWeight: 700, padding: '2px 8px', letterSpacing: '0.15em', borderRadius: 1 }}>{plan.badge}</div>
                  )}
                  <div>
                    <div style={{ fontFamily: 'var(--display)', fontSize: 13, fontWeight: 700, color: selected === plan.id ? 'var(--green)' : 'var(--text)', letterSpacing: '0.1em' }}>{plan.label}</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>{plan.description}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 900, color: selected === plan.id ? 'var(--green)' : 'var(--text-muted)', textShadow: selected === plan.id ? 'var(--glow)' : 'none' }}>{plan.price}</span>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)' }}>{plan.period}</span>
                  </div>
                </div>
              ))}
            </div>

            {error && <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--red)', marginBottom: 14 }}>✗ {error}</div>}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={{ padding: '10px 18px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 1, color: 'var(--text-muted)', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em' }}>CANCEL</button>
              <button onClick={handleSubscribe} disabled={loading}
                style={{ flex: 1, padding: '10px 0', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', borderRadius: 1, color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: 11, cursor: 'pointer', letterSpacing: '0.12em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {loading
                  ? <><div style={{ width: 12, height: 12, border: '2px solid var(--green-dim)', borderTopColor: 'var(--green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />REDIRECTING...</>
                  : '→ SUBSCRIBE WITH STRIPE'
                }
              </button>
            </div>
          </>
        )}

        {!isSubscribed && (
          <div style={{ marginTop: 14, fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', textAlign: 'center', lineHeight: 1.7 }}>
            🔒 Payments processed securely by Stripe · Cancel anytime from the billing portal
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────

export default function App() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useAuth();

  const [userProfile, setUserProfile] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [mode, setMode] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [preflightMessage, setPreflightMessage] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [showToS, setShowToS] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // Check for Stripe success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('session_id')) {
      // Payment succeeded — refresh profile to get updated credits
      window.history.replaceState({}, '', window.location.pathname);
      if (isSignedIn) syncProfile();
    }
  }, [isSignedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync profile to Supabase when user signs in
  useEffect(() => {
    if (isSignedIn && user) syncProfile();
  }, [isSignedIn, user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function syncProfile() {
    setCreditsLoading(true);
    try {
      const headers = await getAuthHeader(getToken);
      const res = await fetch(`${API_BASE_URL}/auth/sync`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: user?.fullName || user?.username }),
      });
      if (res.ok) {
        const data = await res.json();
        setUserProfile(data.profile);
      }
    } catch (e) { console.warn('Profile sync failed', e); }
    finally { setCreditsLoading(false); }
  }

  function handleCreditsUpdate(newTotal) {
    // For free-tier users, update remaining credit count from API response
    setUserProfile(p => p ? { ...p, total_credits: newTotal } : p);
  }

  async function handleFile(f) {
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setError(null);
    setPreflightMessage(null);
    setLoading(true);

    try {
      const headers = await getAuthHeader(getToken);
      const form = new FormData();
      form.append('image', f);
      const res = await fetch(`${API_BASE_URL}/identify`, { method: 'POST', headers, body: form });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 402) throw new Error('INSUFFICIENT_CREDITS');
        if (data.error === 'IMAGE_TOO_LARGE') throw new Error('IMAGE_TOO_LARGE');
        if (data.code === 'PREFLIGHT_FAILED') {
          const err = new Error(data.error || 'PREFLIGHT_FAILED');
          err.preflightMessage = data.message;
          throw err;
        }
        throw new Error(data.error || `Server error: ${res.status}`);
      }
      setResult(data);
      if (data.credits_remaining !== undefined) handleCreditsUpdate(data.credits_remaining);
      if (data.subscribed !== undefined) setUserProfile(p => p ? { ...p } : p);
    } catch (err) {
      setError(err.message);
      if (err.preflightMessage) setPreflightMessage(err.preflightMessage);
    } finally {
      setLoading(false);
    }
  }

  // ── Auth loading splash ───────────────────────────────────────────────────
  if (!isLoaded) return (
    <div style={{ minHeight: '100vh', background: '#070a06', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{css}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 900, color: '#39FF14', letterSpacing: '0.2em', textShadow: '0 0 24px rgba(57,255,20,0.5)', marginBottom: 20 }}>NODE // REVEAL</div>
        <div style={{ width: 120, height: 1, background: 'rgba(57,255,20,0.2)', margin: '0 auto', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, #39FF14, transparent)', animation: 'progress-bar 1.8s ease infinite' }} />
        </div>
      </div>
    </div>
  );

  // ── Login gate ────────────────────────────────────────────────────────────
  if (!isSignedIn) return <AuthScreen />;

  // ── Enhancement module ────────────────────────────────────────────────────
  if (mode === 'enhance') return (
    <>
      <style>{css}</style>
      <EnhancementScreen
        onBack={() => setMode(null)}
        credits={userProfile}
        onCreditsUpdate={handleCreditsUpdate}
        getToken={getToken}
        onSubscribe={() => setShowSubscribe(true)}
      />
      {showSubscribe && <SubscribeModal onClose={() => setShowSubscribe(false)} getToken={getToken} currentProfile={userProfile} />}
    </>
  );

  // ── Shared header / footer / logo ─────────────────────────────────────────
  const PixelLogo = () => (
    <svg width="26" height="26" viewBox="0 0 11 11" style={{ imageRendering: 'pixelated', filter: 'drop-shadow(0 0 3px #39FF14)' }} fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="0" width="1" height="1" fill="#39FF14"/><rect x="2" y="0" width="1" height="1" fill="#39FF14"/><rect x="3" y="0" width="1" height="1" fill="#39FF14"/><rect x="4" y="0" width="1" height="1" fill="#39FF14"/>
      <rect x="0" y="1" width="1" height="1" fill="#39FF14"/><rect x="5" y="1" width="1" height="1" fill="#39FF14"/><rect x="0" y="2" width="1" height="1" fill="#39FF14"/><rect x="5" y="2" width="1" height="1" fill="#39FF14"/>
      <rect x="0" y="3" width="1" height="1" fill="#39FF14"/><rect x="5" y="3" width="1" height="1" fill="#39FF14"/><rect x="0" y="4" width="1" height="1" fill="#39FF14"/><rect x="5" y="4" width="1" height="1" fill="#39FF14"/>
      <rect x="1" y="5" width="1" height="1" fill="#39FF14"/><rect x="2" y="5" width="1" height="1" fill="#39FF14"/><rect x="3" y="5" width="1" height="1" fill="#39FF14"/><rect x="4" y="5" width="1" height="1" fill="#39FF14"/>
      <rect x="1" y="1" width="1" height="1" fill="#39FF14" opacity="0.3"/><rect x="2" y="1" width="1" height="1" fill="#39FF14" opacity="0.15"/>
      <rect x="5" y="5" width="1" height="1" fill="#39FF14"/><rect x="6" y="6" width="1" height="1" fill="#39FF14"/><rect x="7" y="7" width="1" height="1" fill="#39FF14"/><rect x="8" y="8" width="1" height="1" fill="#39FF14"/><rect x="9" y="9" width="1" height="1" fill="#39FF14"/>
      <rect x="6" y="5" width="1" height="1" fill="#39FF14"/><rect x="7" y="6" width="1" height="1" fill="#39FF14"/><rect x="8" y="7" width="1" height="1" fill="#39FF14"/><rect x="9" y="8" width="1" height="1" fill="#39FF14"/><rect x="10" y="9" width="1" height="1" fill="#39FF14"/>
    </svg>
  );

  const SharedHeader = ({ showBack }) => (
    <header style={{ borderBottom: '1px solid var(--border)', padding: '0 28px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--black)', position: 'sticky', top: 0, zIndex: 100 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ width: 40, height: 40, border: '1px solid var(--green-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--green-faint)', flexShrink: 0 }}>
          <PixelLogo />
        </div>
        <div>
          <div style={{ fontFamily: 'var(--display)', fontSize: 16, fontWeight: 900, color: 'var(--green)', letterSpacing: '0.15em', textShadow: 'var(--glow)', animation: 'flicker 12s ease infinite' }}>NODE // REVEAL</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.15em', textTransform: 'uppercase' }}>AI Image Analysis</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <SubscriptionBadge profile={userProfile} loading={creditsLoading} />
        <button onClick={() => setShowSubscribe(true)}
          style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--green)', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', borderRadius: 1, padding: '5px 12px', cursor: 'pointer', letterSpacing: '0.1em' }}>
          {userProfile?.subscription_tier && userProfile.subscription_tier !== 'free' ? '⚙ SUBSCRIPTION' : '↑ SUBSCRIBE'}
        </button>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', letterSpacing: '0.08em', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {user?.primaryEmailAddress?.emailAddress}
        </span>
        {showBack && (
          <button onClick={() => { setMode(null); setResult(null); setFile(null); setPreview(null); setError(null); }}
            style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', background: 'transparent', border: '1px solid var(--border)', borderRadius: 1, padding: '5px 12px', cursor: 'pointer', letterSpacing: '0.1em', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.target.style.color = 'var(--green)'; e.target.style.borderColor = 'var(--green-dim)'; }}
            onMouseLeave={e => { e.target.style.color = 'var(--text-muted)'; e.target.style.borderColor = 'var(--border)'; }}
          >← MODULES</button>
        )}
        <button onClick={() => setShowFeedbackModal(true)}
          style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', background: 'transparent', border: '1px solid var(--border)', borderRadius: 1, padding: '5px 12px', cursor: 'pointer', letterSpacing: '0.1em', transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--green)'; e.currentTarget.style.borderColor = 'var(--green-dim)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
        >✉ FEEDBACK</button>
        <button onClick={() => signOut()}
          style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', background: 'transparent', border: '1px solid rgba(57,255,20,0.1)', borderRadius: 1, padding: '5px 12px', cursor: 'pointer', letterSpacing: '0.1em', transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--red)'; e.currentTarget.style.borderColor = 'rgba(255,59,59,0.3)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-dim)'; e.currentTarget.style.borderColor = 'rgba(57,255,20,0.1)'; }}
        >⏻ SIGN OUT</button>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', boxShadow: 'var(--glow)', animation: 'pulse-green 2s ease infinite' }} />
      </div>
    </header>
  );

  const SharedFooter = () => (
    <footer style={{ borderTop: '1px solid var(--border)', padding: '14px 28px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.15em' }}>© 2026 OPERATION // NODE INC. — 501(c)(3) NONPROFIT</span>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <button onClick={() => setShowToS(true)} style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', background: 'transparent', border: 'none', cursor: 'pointer', letterSpacing: '0.1em', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 }}>TERMS OF SERVICE</button>
          <button onClick={() => setShowPrivacy(true)} style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', background: 'transparent', border: 'none', cursor: 'pointer', letterSpacing: '0.1em', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 }}>PRIVACY POLICY</button>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.1em' }}>NODE // REVEAL v2.0</span>
        </div>
      </div>
      <p style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.05em', lineHeight: 1.6, textAlign: 'center' }}>
        ⚠ FOR INVESTIGATIVE ASSISTANCE ONLY — AI-GENERATED RESULTS ARE PROBABILISTIC AND MAY BE INCORRECT. ALL IDENTIFICATIONS MUST BE INDEPENDENTLY VALIDATED BEFORE USE IN ANY OFFICIAL CAPACITY.
      </p>
    </footer>
  );

  // ── HOME / MODULE SELECT SCREEN ───────────────────────────────────────────
  if (!mode) return (
    <>
      <style>{css}</style>
      {showFeedbackModal && <GeneralFeedbackModal onClose={() => setShowFeedbackModal(false)} />}
      {showSubscribe && <SubscribeModal onClose={() => setShowSubscribe(false)} getToken={getToken} currentProfile={userProfile} />}
      {showToS && <LegalModal type="tos" onClose={() => setShowToS(false)} />}
      {showPrivacy && <LegalModal type="privacy" onClose={() => setShowPrivacy(false)} />}
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
        <SharedHeader showBack={false} />
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px' }}>

          <div style={{ textAlign: 'center', marginBottom: 48, animation: 'fadeUp 0.5s ease' }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: 12 }}>SELECT ANALYSIS MODULE</div>
            <h1 style={{ fontFamily: 'var(--display)', fontSize: 28, fontWeight: 900, color: 'var(--text)', letterSpacing: '0.08em', lineHeight: 1.2, marginBottom: 14 }}>
              WHAT ARE YOU<br/><span style={{ color: 'var(--green)', textShadow: 'var(--glow)' }}>ANALYZING?</span>
            </h1>
            <p style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', maxWidth: 440, margin: '0 auto', lineHeight: 1.8 }}>
              Select an analysis module below. Each module uses specialized AI prompting and reference databases optimized for that target type.
            </p>
          </div>

          {/* Mode Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, width: '100%', maxWidth: 1100, animation: 'fadeUp 0.5s ease 0.1s both' }}>

            {/* ── VEHICLE — Active ── */}
            <div onClick={() => setMode('vehicle')} style={{ border: '1px solid var(--border-bright)', borderRadius: 2, background: 'var(--bg-card)', padding: '36px 28px', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--green-faint)'; e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.boxShadow = 'var(--glow-strong)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.borderColor = 'var(--border-bright)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--green), transparent)' }} />
              {['topleft','topright','bottomleft','bottomright'].map(pos => (
                <div key={pos} style={{ position: 'absolute', width: 10, height: 10, [pos.includes('top') ? 'top' : 'bottom']: 8, [pos.includes('left') ? 'left' : 'right']: 8, borderTop: pos.includes('top') ? '1px solid var(--green-dim)' : 'none', borderBottom: pos.includes('bottom') ? '1px solid var(--green-dim)' : 'none', borderLeft: pos.includes('left') ? '1px solid var(--green-dim)' : 'none', borderRight: pos.includes('right') ? '1px solid var(--green-dim)' : 'none' }} />
              ))}
              <div style={{ marginBottom: 20 }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ filter: 'drop-shadow(0 0 6px #39FF14)' }}>
                  <rect x="2" y="8" width="20" height="10" rx="1" stroke="#39FF14" strokeWidth="1.5"/>
                  <circle cx="6" cy="18" r="2.5" stroke="#39FF14" strokeWidth="1.5"/>
                  <circle cx="18" cy="18" r="2.5" stroke="#39FF14" strokeWidth="1.5"/>
                  <path d="M2 12h1M21 12h1M6 8l2-4h8l2 4" stroke="#39FF14" strokeWidth="1.5" strokeLinecap="round"/>
                  <rect x="9" y="10" width="6" height="3" rx="0.5" stroke="#39FF14" strokeWidth="1" opacity="0.5"/>
                </svg>
              </div>
              <div style={{ fontFamily: 'var(--display)', fontSize: 15, fontWeight: 700, color: 'var(--green)', letterSpacing: '0.12em', marginBottom: 10, textShadow: 'var(--glow)' }}>VEHICLE ANALYSIS</div>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 20 }}>
                Identify make, model, year, trim, and color from any vehicle image. Cross-referenced against a database of 500+ generations and OEM paint codes.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
                {['Make & Model', 'Year / Gen', 'Trim Level', 'OEM Color', 'License Plate'].map(f => (
                  <span key={f} style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--green)', border: '1px solid var(--green-dim)', padding: '2px 7px', background: 'var(--green-faint)', borderRadius: 1 }}>{f}</span>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', boxShadow: 'var(--glow)', animation: 'pulse-green 2s ease infinite' }} />
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--green)', letterSpacing: '0.1em' }}>ONLINE</span>
                </div>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', border: '1px solid rgba(57,255,20,0.15)', padding: '2px 8px', borderRadius: 1 }}>2 CREDITS</span>
              </div>
            </div>

            {/* ── IMAGE ENHANCEMENT — Active ── */}
            <div onClick={() => setMode('enhance')} style={{ border: '1px solid var(--border-bright)', borderRadius: 2, background: 'var(--bg-card)', padding: '36px 28px', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--green-faint)'; e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.boxShadow = 'var(--glow-strong)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.borderColor = 'var(--border-bright)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--green), transparent)' }} />
              {['topleft','topright','bottomleft','bottomright'].map(pos => (
                <div key={pos} style={{ position: 'absolute', width: 10, height: 10, [pos.includes('top') ? 'top' : 'bottom']: 8, [pos.includes('left') ? 'left' : 'right']: 8, borderTop: pos.includes('top') ? '1px solid var(--green-dim)' : 'none', borderBottom: pos.includes('bottom') ? '1px solid var(--green-dim)' : 'none', borderLeft: pos.includes('left') ? '1px solid var(--green-dim)' : 'none', borderRight: pos.includes('right') ? '1px solid var(--green-dim)' : 'none' }} />
              ))}
              <div style={{ marginBottom: 20 }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" style={{ filter: 'drop-shadow(0 0 6px #39FF14)' }}>
                  <rect x="3" y="3" width="18" height="18" rx="1" stroke="#39FF14" strokeWidth="1.5"/>
                  <path d="M3 15l4-4 3 3 4-5 7 8" stroke="#39FF14" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="8" cy="8" r="1.5" stroke="#39FF14" strokeWidth="1.2"/>
                  <path d="M17 7l1 1-1 1M15 7l-1 1 1 1" stroke="#39FF14" strokeWidth="1" strokeLinecap="round" opacity="0.6"/>
                  <path d="M16 6v6" stroke="#39FF14" strokeWidth="1" strokeLinecap="round" opacity="0.4"/>
                </svg>
              </div>
              <div style={{ fontFamily: 'var(--display)', fontSize: 15, fontWeight: 700, color: 'var(--green)', letterSpacing: '0.12em', marginBottom: 10, textShadow: 'var(--glow)' }}>IMAGE ENHANCEMENT</div>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 20 }}>
                Clean up and sharpen images without running full analysis. Noise reduction, contrast correction, and detail recovery for standard photos and CCTV footage.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
                {['Noise Reduction', 'Contrast Fix', 'Detail Recovery', 'CCTV Mode', 'Download'].map(f => (
                  <span key={f} style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--green)', border: '1px solid var(--green-dim)', padding: '2px 7px', background: 'var(--green-faint)', borderRadius: 1 }}>{f}</span>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', boxShadow: 'var(--glow)', animation: 'pulse-green 2s ease infinite' }} />
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--green)', letterSpacing: '0.1em' }}>ONLINE</span>
                </div>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', border: '1px solid rgba(57,255,20,0.15)', padding: '2px 8px', borderRadius: 1 }}>1 CREDIT</span>
              </div>
            </div>

            {/* ── WEAPON — Coming Soon ── */}
            <div style={{ border: '1px solid rgba(57,255,20,0.08)', borderRadius: 2, background: 'rgba(11,15,10,0.5)', padding: '36px 28px', cursor: 'not-allowed', position: 'relative', overflow: 'hidden', opacity: 0.5 }}>
              <div style={{ position: 'absolute', top: 22, right: -30, background: 'var(--amber)', color: 'var(--black)', fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', padding: '4px 44px', transform: 'rotate(35deg)', textTransform: 'uppercase', boxShadow: '0 2px 8px rgba(0,0,0,0.5)', zIndex: 2 }}>
                COMING SOON
              </div>
              <div style={{ marginBottom: 20 }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="10" width="11" height="4" rx="0.5" stroke="rgba(57,255,20,0.4)" strokeWidth="1.5"/>
                  <path d="M14 12h3l2 2v-4l-2 2" stroke="rgba(57,255,20,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 10V7M9 10V7" stroke="rgba(57,255,20,0.3)" strokeWidth="1.2" strokeLinecap="round"/>
                  <rect x="5" y="14" width="2" height="3" rx="0.5" stroke="rgba(57,255,20,0.3)" strokeWidth="1"/>
                </svg>
              </div>
              <div style={{ fontFamily: 'var(--display)', fontSize: 15, fontWeight: 700, color: 'rgba(57,255,20,0.35)', letterSpacing: '0.12em', marginBottom: 10 }}>WEAPON ANALYSIS</div>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', lineHeight: 1.8, marginBottom: 20 }}>
                Identify firearms by make, model, caliber, and configuration from images. Designed to support investigative workflows and evidence documentation.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
                {['Make & Model', 'Caliber', 'Configuration', 'Modifications', 'Serial Region'].map(f => (
                  <span key={f} style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(57,255,20,0.25)', border: '1px solid rgba(57,255,20,0.1)', padding: '2px 7px', borderRadius: 1 }}>{f}</span>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--amber)', opacity: 0.5 }} />
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-dim)', letterSpacing: '0.1em' }}>IN DEVELOPMENT</span>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div style={{ marginTop: 32, maxWidth: 1100, width: '100%', animation: 'fadeUp 0.5s ease 0.2s both' }}>
            <div style={{ border: '1px solid rgba(255,184,0,0.3)', background: 'rgba(255,184,0,0.05)', borderRadius: 2, padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--amber)', fontSize: 14, flexShrink: 0 }}>⚠</span>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--amber)', lineHeight: 1.8, letterSpacing: '0.04em', opacity: 0.9 }}>
                NODE // REVEAL IS ACTIVELY IN DEVELOPMENT. This tool uses AI to analyze images — results are probabilistic and may be incorrect. All identifications must be independently validated before use in any official capacity.
              </p>
            </div>
          </div>
        </main>
        <SharedFooter />
      </div>
    </>
  );

  // ── ANALYSIS SCREEN ───────────────────────────────────────────────────────
  return (
    <>
      <style>{css}</style>
      {showFeedbackModal && <GeneralFeedbackModal onClose={() => setShowFeedbackModal(false)} />}
      {showSubscribe && <SubscribeModal onClose={() => setShowSubscribe(false)} getToken={getToken} currentProfile={userProfile} />}
      {showToS && <LegalModal type="tos" onClose={() => setShowToS(false)} />}
      {showPrivacy && <LegalModal type="privacy" onClose={() => setShowPrivacy(false)} />}
      <LoadingOverlay active={loading} />
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
        <SharedHeader showBack={true} />

        {/* ── MAIN ── */}
        <main style={{ flex: 1, maxWidth: 1100, width: '100%', margin: '0 auto', padding: '28px 20px', display: 'grid', gridTemplateColumns: result ? '1fr 1fr' : '1fr', gap: 20, alignItems: 'start' }}>

          {/* LEFT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* ── DISCLAIMER BANNER ── */}
            <div style={{ border: '1px solid rgba(255,184,0,0.3)', background: 'rgba(255,184,0,0.05)', borderRadius: 2, padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--amber)', fontSize: 14, flexShrink: 0 }}>⚠</span>
              <p style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--amber)', lineHeight: 1.8, letterSpacing: '0.04em', opacity: 0.9 }}>
                NODE // REVEAL IS ACTIVELY IN DEVELOPMENT. This tool uses AI to analyze images — results are probabilistic and may be incorrect. All identifications must be independently validated before use in any official capacity.
              </p>
            </div>

            {/* Upload */}
            <UploadZone onFile={handleFile} loading={loading} />

            {/* Image preview / comparison */}
            {result && (
              <div style={{ animation: 'fadeUp 0.4s ease' }}>
                <ComparisonSlider original={result.original_b64} enhanced={result.enhanced_b64} />
                {result.enhancement_info && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {result.enhancement_info.enhancementsApplied?.map((e, i) => (
                      <span key={i} style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-muted)', border: '1px solid var(--border-bright)', padding: '4px 10px', borderRadius: 1, background: 'var(--bg-card2)', display: 'inline-block' }}>{e}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Preview before results */}
            {preview && !result && !loading && (
              <div style={{ borderRadius: 2, overflow: 'hidden', border: '1px solid var(--border)' }}>
                <img src={preview} alt="Uploaded" style={{ width: '100%', display: 'block' }} />
              </div>
            )}

            {/* Error */}
            {error && (
              <Card style={{ borderColor: error === 'INSUFFICIENT_CREDITS' ? 'rgba(255,59,59,0.4)' : 'rgba(255,59,59,0.4)', background: 'rgba(255,59,59,0.05)', animation: 'fadeUp 0.3s ease' }}>
                {error === 'INSUFFICIENT_CREDITS' ? (
                  <div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--red)', letterSpacing: '0.1em', marginBottom: 8 }}>✗ INSUFFICIENT CREDITS</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 12 }}>You need 2 credits to run a vehicle analysis.</div>
                    <button onClick={() => setShowSubscribe(true)} style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--green)', background: 'var(--green-faint)', border: '1px solid var(--green-dim)', borderRadius: 1, padding: '6px 16px', cursor: 'pointer', letterSpacing: '0.1em' }}>↑ SUBSCRIBE</button>
                  </div>
                ) : error === 'IMAGE_TOO_LARGE' ? (
                  <div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--red)', letterSpacing: '0.1em', marginBottom: 8 }}>✗ IMAGE TOO LARGE</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.8 }}>Image exceeds the 5MB maximum. Please compress or resize the image before uploading.</div>
                  </div>
                ) : (error === 'NO_VEHICLE' || error === 'UNANALYZABLE' || error === 'VEHICLE_TOO_OCCLUDED') ? (
                  <div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--red)', letterSpacing: '0.1em', marginBottom: 8 }}>
                      {error === 'NO_VEHICLE' ? '✗ NO VEHICLE DETECTED' : error === 'VEHICLE_TOO_OCCLUDED' ? '✗ VEHICLE TOO OCCLUDED' : '✗ IMAGE UNANALYZABLE'}
                    </div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.8 }}>
                      {preflightMessage || 'Unable to process this image. Please try a clearer photo with the vehicle more visible.'}
                    </div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-dim)', marginTop: 8 }}>
                      No credits were deducted for this submission.
                    </div>
                  </div>
                ) : (
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--red)' }}>✗ ERROR: {error}</div>
                )}
              </Card>
            )}
          </div>

          {/* RIGHT COLUMN — Results */}
          {result && (
            <div style={{ animation: 'scanIn 0.5s ease' }}>
              <ResultsPanel result={result} imageHash={result.image_hash} imageFile={file} getToken={getToken} />
            </div>
          )}
        </main>

        <SharedFooter />
      </div>
    </>
  );
}